// src/admin/hooks/useKycQueue.ts
// Feeds the /kyc review queue. Returns pending + manual_review by default;
// the screen can widen the filter to include verified/rejected for audit
// trails. Capped at 200 rows — tune the filter, not the cap.
//
// Schema realities that differ from the plan:
//   - The FK column is `user_id` (references profiles.id), not `borrower_id`.
//   - There is no `nrc_number` column on kyc_submissions; the NRC lives in
//     `ocr_result_jsonb` and we surface it via `nrcFromOcr()` so the queue
//     can show it alongside the borrower's name + phone.
//   - database.types.ts declares `Relationships: []`, so the embedded
//     `profiles(...)` select is typed manually and cast with
//     `as unknown as KycQueueRow[]` on the return.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database, Json } from '@/lib/database.types';
import { nrcFromOcr } from '@admin/utils/kycStatus';

type KycStatus = Database['public']['Enums']['kyc_status'];

export interface KycQueueRow {
  id: string;
  user_id: string;
  status: KycStatus;
  submitted_at: string;
  reviewed_at: string | null;
  ocr_result_jsonb: Json | null;
  nrc_number: string | null; // derived from ocr_result_jsonb
  profiles: { display_name: string | null; full_name: string | null; phone: string | null } | null;
}

export interface KycQueueFilter {
  status?: KycStatus[];
}

export function useKycQueue(filter: KycQueueFilter = {}) {
  return useQuery({
    queryKey: ['kyc-queue', filter],
    queryFn: async (): Promise<KycQueueRow[]> => {
      let q = supabase
        .from('kyc_submissions')
        .select(
          'id, user_id, status, submitted_at, reviewed_at, ocr_result_jsonb, profiles(display_name, full_name, phone)',
        )
        .order('submitted_at', { ascending: false })
        .limit(200);
      if (filter.status && filter.status.length > 0) {
        q = q.in('status', filter.status);
      }
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as unknown as Omit<KycQueueRow, 'nrc_number'>[];
      return rows.map((r) => ({ ...r, nrc_number: nrcFromOcr(r.ocr_result_jsonb) }));
    },
  });
}
